import { useMemo } from 'react';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { IShipmentCustomsStatus } from '@packlink/packlink-sdk';
import { useTranslation } from '@packlink/translation-provider';
import { useQuery } from '@tanstack/react-query';
import { transformCustomsInvoiceToForm, transformClientToTaxForm, transformCustomsClientToForm, getNewCustomsInvoiceItem } from '../data/dataTransformation.js';
import { getInitialClientTaxFormValues } from '../data/TaxFormTypes.js';
import { reasonsToExportOptions } from '../data/ReasonsToExport.js';
import { translateSelectOption } from '../utils/select.js';

const reactQueryOptions = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false
};
const useGetCustomsInitialValues = _ref => {
  let {
    currency,
    sender,
    receiver,
    customs,
    client,
    fetchSavedSenderData,
    validTariffNumbers,
    defaultCustomsCategory,
    defaultExportReason
  } = _ref;
  const sdk = useSdk();
  const {
    t
  } = useTranslation();
  const savedCustomsQuery = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['customs-form', 'saved-customs', customs === null || customs === void 0 ? void 0 : customs.status, customs === null || customs === void 0 ? void 0 : customs.customsInvoiceId],
    queryFn: () => {
      const sdkEntity = (customs === null || customs === void 0 ? void 0 : customs.status) === IShipmentCustomsStatus.DRAFT ? sdk.v1.draftCustomsInvoices : sdk.v1.customsInvoices;
      return sdkEntity.get(customs === null || customs === void 0 ? void 0 : customs.customsInvoiceId);
    },
    ...reactQueryOptions,
    enabled: !!customs
  });
  const senderDataQuery = useQuery({
    queryKey: ['customs-form', 'sender-data'],
    queryFn: () => sdk.v1.customsInvoices.getSenderData().then(senderData => {
      if (!senderData) return null;
      return senderData;
    }),
    ...reactQueryOptions,
    enabled: fetchSavedSenderData
  });
  const initialValues = useMemo(() => {
    var _customs$status;
    if (customs && savedCustomsQuery.data) {
      const formParser = transformCustomsInvoiceToForm({
        currency,
        status: customs.status,
        t,
        validTariffNumbers
      });
      return formParser(savedCustomsQuery.data);
    }
    const senderFallbackValues = client ? transformClientToTaxForm(client, t) : getInitialClientTaxFormValues(sender, t);
    const senderInitialValues = senderDataQuery.data ? transformCustomsClientToForm(senderDataQuery.data.address, senderDataQuery.data.taxInformation, t) : senderFallbackValues;
    return {
      status: (_customs$status = customs === null || customs === void 0 ? void 0 : customs.status) !== null && _customs$status !== void 0 ? _customs$status : IShipmentCustomsStatus.DRAFT,
      inventoryOfContents: [getNewCustomsInvoiceItem({
        defaultCustomsCategory,
        t
      })],
      customsTerms: false,
      currency,
      id: customs === null || customs === void 0 ? void 0 : customs.customsInvoiceId,
      sender: senderInitialValues,
      receiver: getInitialClientTaxFormValues(receiver, t),
      reasonsToExport: getDefaultExportReasonOption(defaultExportReason, t),
      invoiceNumber: undefined,
      authorised: false
    };
  }, [client, currency, customs, defaultCustomsCategory, defaultExportReason, receiver, savedCustomsQuery.data, sender, senderDataQuery.data, t, validTariffNumbers]);
  return [initialValues, savedCustomsQuery.isLoading || senderDataQuery.isLoading];
};
function getDefaultExportReasonOption(exportReason, t) {
  if (!exportReason) {
    return undefined;
  }
  const option = reasonsToExportOptions.find(_ref2 => {
    let {
      value
    } = _ref2;
    return exportReason === value;
  });
  return option ? translateSelectOption(option, t) : undefined;
}

export { useGetCustomsInitialValues };
//# sourceMappingURL=useGetCustomsInitialValues.js.map
